"use client";
import { FC, useEffect, useState, ChangeEvent } from "react";
import { useFormik, FormikHelpers } from "formik";
import { useTransitionRouter } from 'next-view-transitions'
import { Button, TextGroup, Textfield, Alert, Label, Checkbox, Textarea } from "@/components/forms";
import { fetchCMS } from "@/lib/cms";
import { formatDate } from "@/lib/utils";
import {
  FormValues,
  initialValues,
  validate,
  performSubmit,
  DefaultHelpersType,
  defaultHelpers,
  useAlertState,
} from "@/lib/contactForm.utils";
import Styles from "@/app/apply/apply.module.scss";

export const defaults: ApplyFormCopy = {
  cta: "Join the Team",
  successMessage: "Your application has been submitted successfully.",
  submitButtonText: "Submit Application",
  resumeFieldText: "Upload resume",
  activeStatusText: "Active",
  title: "Apply for a job",
  intro: "Join our team on the Santa Monica Pier! We're looking for enthusiastic people who love live entertainment, hospitality, and creating memorable experiences for every guest.",
  alreadyAppliedCopy: "You already applied to this position on {date}.",
  agreementLabel: "I agree to the California Roadhouse Core Values",
};

const PERSISTENT_STATE_KEY = "applicationCompletedAt";

type ApplyFormCopy = {
  cta?: string;
  successMessage?: string;
  submitButtonText?: string;
  resumeFieldText?: string;
  activeStatusText?: string;
  title?: string;
  intro?: string;
  applicationTerms?: string;
  alreadyAppliedCopy?: string;
  agreementLabel?: string;
}

type ApplicationFormProps = {
  content?: ApplyFormCopy;
  position?: string;
};

export const ApplicationForm: FC<ApplicationFormProps> = ({ content, position }) => {
  const [dateApplied, setDateApplied] = useState<string | null>(null);
  const router = useTransitionRouter();

  const onResumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = (e.currentTarget.files && e.currentTarget.files[0]) || null;
    formik.setFieldValue("resume", file);
  };

  const [agreementEnabled, setAgreementEnabled] = useState(false);

  const onLegalScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.target as HTMLElement | null;
    if (!el) return;
    const threshold = 8;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      setAgreementEnabled(true);
    }
  };

  const onSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues> = defaultHelpers as FormikHelpers<FormValues>,
  ) => {
    const { setSubmitting, resetForm, setStatus } = helpers as DefaultHelpersType;
    if (setStatus) setStatus(null);

    setSubmitting(true);
    try {
      const redirect = await performSubmit(values, position);
      resetForm({
        values: initialValues,
        status: content?.successMessage,
      });
      if (redirect) {
        localStorage?.setItem(PERSISTENT_STATE_KEY, new Date().toUTCString());
        router.push(redirect);
      }
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      if (setStatus) setStatus("Failed to send application: " + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues,
    validate,
    onSubmit,
  });

  const { alertTitle, alertText, alertVariant } = useAlertState(
    formik.status,
    formik.touched,
    formik.errors,
  );

  const [cmsContent, setCmsContent] = useState<ApplyFormCopy | null>(null);

  const fetchCMSContent = async () => {
    const { data } = await fetchCMS('contact-form', { populate: '*' });
    if (!data) return;
    setCmsContent(data);
  };

  useEffect(() => {
    const applicationCompletedAt = localStorage?.getItem(PERSISTENT_STATE_KEY) || null;
    if (applicationCompletedAt) {
      setDateApplied(formatDate(applicationCompletedAt));
    }

    if (!content) {
      fetchCMSContent();
    }
  }, [content]);

  const effectiveContent = content ?? cmsContent;

  return (
    <form className={Styles.form} onSubmit={formik.handleSubmit} noValidate>
      {dateApplied && (
        <Alert persist variant="success">
          {effectiveContent?.alreadyAppliedCopy?.replace("{date}", dateApplied)}
        </Alert>
      )}
      {position && (
        <input type="hidden" name="position" value={position} />
      )}
      <TextGroup
        label="Name"
        className={Styles.textGroup}
        size="lg"
        textProps={{
          name: "name",
          value: formik.values.name,
          onChange: formik.handleChange,
          onBlur: formik.handleBlur,
          required: true,
          error: Boolean(formik.touched.name && formik.errors.name),
        }}
      />

      <TextGroup
        label="Email"
        className={Styles.textGroup}
        size="lg"
        textProps={{
          name: "email",
          type: "email",
          value: formik.values.email,
          onChange: formik.handleChange,
          onBlur: formik.handleBlur,
          required: true,
          error: Boolean(formik.touched.email && formik.errors.email),
        }}
      />

      <TextGroup
        label="Phone"
        className={Styles.textGroup}
        size="lg"
        textProps={{
          name: "phone",
          value: formik.values.phone,
          onChange: formik.handleChange,
          onBlur: formik.handleBlur,
          required: false,
          error: Boolean(formik.touched.phone && formik.errors.phone),
        }}
      />

      <Label htmlFor="resume" klass="uploader">
        {formik.values.resume?.name || effectiveContent?.resumeFieldText }
      </Label>

      <Textfield
        id="resume"
        name="resume"
        type="file"
        klass="hidden"
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={onResumeChange}
      />

      <Textarea
        id="legalText"
        formatted={true}
        readOnly
        size="xs"
        onScroll={onLegalScroll}
        className={Styles.legalText}
        aria-label="Legal agreement text"
        value={effectiveContent?.applicationTerms}
      />

      <Checkbox
        id="agreed"
        disabled={!agreementEnabled}
        checked={formik.values.agreed}
        onChange={(e) => formik.setFieldValue("agreed", e.target.checked)}
        label={effectiveContent?.agreementLabel ?? "I agree to the California Roadhouse Core Values"}
      />

      <Alert persist variant={alertVariant} title={alertTitle}>
        {alertText}
      </Alert>

      <Button
        type="submit"
        size="lg"
        variant="primary"
        disabled={formik.isSubmitting}
        loading={formik.isSubmitting}
        value={formik.isSubmitting ? effectiveContent?.activeStatusText : effectiveContent?.submitButtonText}
      />
    </form>
  );
};

export default ApplicationForm;
